import type { MockClient, MockEnvironment } from '@gotti/test-utils';
import { registerClientCreationHook } from '@gotti/test-utils';
import type { PlaygroundPairModule } from '../config/pair.js';
import type {
  PlaygroundModule,
  PlaygroundDefinitionModule,
  PlaygroundMode,
  PlaygroundSetupHandler,
} from '../types.js';
import { bootPlaygroundSelection, resolvePlaygroundSelection, resolveTarget } from './boot.js';
import { maybeRunPlaygroundTests } from './tests.js';
import { PLAYGROUND_GLOBALS, readPlaygroundGlobal, writePlaygroundGlobal } from '../protocol.js';

/**
 * What the generated entry hands `bootstrapPlayground`: the config module, the definition module (or, for an
 * inline entry of the config, its `member`; for a pair, which half — `variant`), an optional setup module, the
 * playground's name, and the element (or selector) to render into.
 */
export interface BootstrapPlaygroundOptions {
  module: PlaygroundModule | { default?: PlaygroundModule };
  // `any` game types: this slot takes definitions and pairs of every game (see `PlaygroundDefinitionModule`).
  definition?: PlaygroundDefinitionModule | PlaygroundPairModule<any, any, any> | { default?: PlaygroundDefinitionModule | PlaygroundPairModule<any, any, any> };
  /** For a pair: which half to boot. Default `'online'`. */
  variant?: PlaygroundMode;
  /** For an inline entry of the config's `playgrounds` (its key, or its `name` in the array form): boot that entry instead of `definition`. */
  member?: string;
  setup?: PlaygroundSetupHandler;
  playgroundName: string;
  target?: string | HTMLElement;
}

let mocksReady = false;


const ensureNodeMocks = async () => {
  if (mocksReady) return;
  mocksReady = true;
};

/** The canvas `client` draws on — its `envOptions.canvas` — when it has one. */
const canvasOf = (client: unknown): HTMLCanvasElement | undefined =>
  (client as { envOptions?: { canvas?: HTMLCanvasElement } } | undefined)?.envOptions?.canvas;

/**
 * The canvas sets of the bootstraps running in this process now — started, not finished: where `recordClientCanvas`
 * writes. A page runs one bootstrap. A test file runs one after another, and one whose test timed out (or whose step
 * was abandoned past its budget) can still be running — or still have work running — when the next test's starts.
 */
const runningClientCanvasSets = new Set<Set<HTMLCanvasElement>>();

/**
 * Records the canvas of each client test-utils creates into the set of the bootstrap running when it is made, for
 * `--video`'s frame gate. One function for the whole process, which every bootstrap registers as it registers the input
 * scoping: registering it again is a no-op, so bootstraps never pile hooks up. A client is attributed only while exactly
 * one bootstrap runs: one made while two overlap could be either one's and is recorded by neither, and one made when
 * none runs — work a finished run left behind — by none. A canvas missing from a list fails loudly; one listed in the
 * wrong bootstrap's would be judged as that playground's world.
 */
const recordClientCanvas = (client: MockClient): void => {
  const canvas = canvasOf(client);
  if (!canvas || runningClientCanvasSets.size !== 1) return;
  for (const running of runningClientCanvasSets) running.add(canvas);
};

/**
 * The canvases the playground's clients draw on — each one's `envOptions.canvas` — once each: those of the clients
 * test-utils created in the page since the boot (`created`, in creation order), then those of any client of
 * `environment` it did not see created, in client order. A client kept under two ids is one, two clients drawing on one
 * canvas count once, and a client without a canvas adds none. What `--video`'s frame gate asks the page for
 * (`PLAYGROUND_GLOBALS.clientCanvases`).
 */
const clientCanvasesOf = (created: ReadonlySet<HTMLCanvasElement>, environment: MockEnvironment | null): HTMLCanvasElement[] => {
  // A Set of canvases, so the client a map lists twice — or two clients drawing on one canvas — counts once.
  const canvases = new Set(created);
  for (const client of Object.values(environment?.clients ?? {})) {
    const canvas = canvasOf(client);
    if (canvas) canvases.add(canvas);
  }
  return [...canvases];
};

/**
 * Boots a playground in the page: resolves config + definition, runs `setup` (or the default environment build),
 * composes game hooks, publishes the runtime context and the READY signal, then runs the test suite when the
 * runner asked for it. Resolves to the `MockEnvironment` once everything — including the suite — has finished.
 */
export const bootstrapPlayground = async (
  options: BootstrapPlaygroundOptions
): Promise<MockEnvironment | null> => {
  if (typeof window !== 'undefined' && readPlaygroundGlobal(window, PLAYGROUND_GLOBALS.bootstrapped)) {
    console.warn('[gotti][playground] Bootstrap called multiple times. Skipping.');
    return null;
  }
  if (typeof window !== 'undefined') {
    writePlaygroundGlobal(window, PLAYGROUND_GLOBALS.bootstrapped, true);
  }

  await ensureNodeMocks();

  const selection = await resolvePlaygroundSelection(options);

  // Each client's canvas is recorded as it is created, into this bootstrap's set while it runs, for `--video`'s frame
  // gate: the world a client draws counts whoever created the client — a `setup` that keeps its environment to itself, a
  // step after READY — not only the clients of the environment the runtime is handed. The recorder is the process's one
  // (`recordClientCanvas`), registered again here as a no-op, as the input scoping is.
  const createdClientCanvases = new Set<HTMLCanvasElement>();
  runningClientCanvasSets.add(createdClientCanvases);
  registerClientCreationHook(recordClientCanvas);
  try {
    const targetEl = resolveTarget(options.target);
    const environment = await bootPlaygroundSelection(selection, targetEl);

    // READY means "the environment is up and the page can be driven". It is published BEFORE the
    // suite runs — never on resolve, because this function awaits the whole suite below — so the
    // runner's serve clock measures serve time and test time stays on the test clock. The client
    // canvases go first: `--video`'s frame gate asks for them the moment it sees READY.
    if (typeof window !== 'undefined') {
      writePlaygroundGlobal(window, PLAYGROUND_GLOBALS.clientCanvases, () => clientCanvasesOf(createdClientCanvases, environment));
      writePlaygroundGlobal(window, PLAYGROUND_GLOBALS.ready, true);
    }

    await maybeRunPlaygroundTests(selection, environment, targetEl);

    return environment;
  } finally {
    // Finished — the suite done, or the boot failed: a client created from now on is no client of this run.
    runningClientCanvasSets.delete(createdClientCanvases);
  }
};
